import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { DEFAULT_ESTIMATE_STATUSES, isTransitionAllowed } from '../lib/constants';
import { getEstimateStatus } from '../utils';
import { Button } from '../components/ui';
import { dateKeysOf, fmtMoveDate } from '../lib/moves';

// useEstimateStatusChange — shared confirmation dialog for estimate status
// changes. Centralizes the safeguard logic so every UI path that changes
// an estimate's status (EstimateModal, EstimateExpandedDetails on the
// Estimates page, ClientsPage pipeline pickers, etc.) routes through the
// SAME warning behavior.
//
// Background: Jaylen reported (2026-05-12) that toggling estimate status
// from BOOKED→Quoted→BOOKED could create duplicate move_jobs rows AND
// trigger crew SMS/push notifications without warning. We added an
// idempotent guard on the server side (db.upsertEstimate skips the
// auto-push when move_jobs already exist) and a confirmation dialog in
// EstimateModal. This hook extracts the dialog logic so it's not stuck
// in just one place — every status change should ride the same rails.
//
// 2026-09-18 (moves-architecture.md §3.4 + showroom pick 2B): booking is a
// commitment to the JOB, not the calendar. The old "Date TBD" gate
// (clientTentative / clearTentative) is gone — booking never refuses on a
// tentative date. Instead the Book confirm carries the move's date chips:
// tap to flip ✓ confirmed / ~ tentative, primary "Book — n confirmed"
// logs those confirmations (confirmMoveDates) then books, secondary
// "Book, confirm later" books without touching the dates.
//
// Usage:
//   const { request, dialog, saving } = useEstimateStatusChange({
//     currentStatus: estimate.status,
//     commit: async (newStatus) => { await saveEstimate({ ...estimate, status: newStatus }); },
//     isLocked: false,           // optional: blocks all transitions
//     move,                      // optional: the estimate's move row → date chips on Book
//     confirmDates: keys => confirmMoveDates(move.id, keys, byName), // optional, async
//   });
//
//   // In JSX:
//   <button onClick={() => request('booked')}>Mark Booked</button>
//   {dialog}   // portal-rendered confirmation modal when one is open

// ─── Warning copy ───────────────────────────────────────────────────────

// Compute the confirmation payload for a proposed transition, or null
// if the move is benign and should commit without prompting.
//
// Returns: null | { title, body, kind: 'normal'|'danger', confirmLabel }
export function getEstimateStatusTransitionWarning(fromKey, toKey) {
    if (!fromKey || !toKey || fromKey === toKey) return null;

    const allowed = isTransitionAllowed(DEFAULT_ESTIMATE_STATUSES, fromKey, toKey);
    const toLabel = getEstimateStatus(toKey).label;
    const fromLabel = getEstimateStatus(fromKey).label;

    // Going TO booked. First-time booking pushes phases to dispatch as
    // unassigned jobs; re-booking (with jobs already there) silently
    // no-ops the push via the server-side count gate. Tentative dates
    // never block this (rule 4) — the dialog shows the date chips instead.
    if (toKey === 'booked') {
        return {
            title: 'Mark as Booked?',
            body: `Pushes the estimate's planned phases to the dispatch board as unassigned jobs (if they're not already there). No crew SMS or push notification fires yet — that happens when a dispatcher schedules the job.`,
            kind: 'normal',
            confirmLabel: 'Mark as Booked',
        };
    }

    // Postponing (2026-09-21): the job is on hold with no date. The legs
    // come off the board, the move's dates go tentative, the quote and
    // addresses stay. Booking it again re-pushes the legs and asks about
    // the dates (chips).
    if (toKey === 'postponed') {
        const wasBooked = fromKey === 'booked';
        return {
            title: wasBooked ? 'Postpone this booked job?' : 'Mark as Postponed?',
            body: wasBooked
                ? `The job's legs are removed from the dispatch board and its dates are marked tentative. The quote, addresses and signed terms stay on file. When they reschedule, set a date and mark it Booked again — the legs come back then.`
                : `The dates are marked tentative and the quote drops out of the capacity count. It stays on file; move it back to Quoted or Booked when they reschedule.`,
            kind: wasBooked ? 'danger' : 'normal',
            confirmLabel: wasBooked ? 'Yes, postpone' : 'Mark as Postponed',
        };
    }

    // Going AWAY from booked — strongly warn. This is the direction that
    // motivated the safeguards: existing dispatched jobs don't auto-clean,
    // and the audit log will record every flip.
    if (fromKey === 'booked') {
        const toCancelled = toKey === 'cancelled';
        return {
            title: toCancelled ? 'Cancel this booked estimate?' : `Revert from Booked to ${toLabel}?`,
            body: toCancelled
                ? `Existing jobs on the dispatch board WILL NOT be removed automatically. If you want to clear them, do so manually via the dispatch board first. The audit log will record this change.`
                : `This is unusual. Existing jobs on the dispatch board will NOT be removed, and the audit log will show every status flip. If a customer pulled back, consider 'Cancelled' instead of '${toLabel}'. Continue only if you're sure.`,
            kind: 'danger',
            confirmLabel: toCancelled ? 'Yes, cancel' : `Yes, revert to ${toLabel}`,
        };
    }

    // Going to cancelled from non-booked: terminal status, worth a quick
    // confirmation.
    if (toKey === 'cancelled') {
        return {
            title: 'Mark as Cancelled?',
            body: `This marks the deal as lost. The audit log will record who cancelled and when.`,
            kind: 'normal',
            confirmLabel: 'Yes, cancel',
        };
    }

    // Disallowed transitions that aren't booked-related.
    if (!allowed) {
        return {
            title: 'Unusual transition',
            body: `Moving from ${fromLabel} to ${toLabel} isn't a normal step in the estimate pipeline. The status will still change, but consider whether the previous status better reflects reality.`,
            kind: 'normal',
            confirmLabel: `Yes, change to ${toLabel}`,
        };
    }

    return null;
}

// ─── Date chips ─────────────────────────────────────────────────────────
// The move's dates as toggles: ✓ confirmed with the client (emerald) or
// ~ still tentative (amber, dashed). Shared by this hook's Book confirm and
// the Estimates page's quick-path status dialog (2026-09-18) so both
// booking paths ask the same question. `state` = { load: true, … }.
export function DateConfirmChips({ dateKeys, state, onChange, disabled }) {
    if (!dateKeys?.length) return null;
    return (
        <div className="space-y-1.5">
            <div className="flex items-center gap-1.5 flex-wrap" role="group" aria-label="Dates to confirm">
                {dateKeys.map(k => {
                    const on = !!state[k.key];
                    const dateLabel = fmtMoveDate(k.date, { windowEnd: k.windowEnd });
                    return (
                        <button
                            key={k.key}
                            type="button"
                            aria-pressed={on}
                            disabled={disabled}
                            onClick={() => onChange({ ...state, [k.key]: !state[k.key] })}
                            title={on ? `${k.label} ${dateLabel} — confirmed with the client (tap to mark tentative)` : `${k.label} ${dateLabel} — still tentative (tap to confirm)`}
                            className={`h-8 px-2.5 rounded-lg border text-[12.5px] font-bold inline-flex items-center gap-1.5 whitespace-nowrap transition-colors ${on
                                ? 'bg-emerald-50 text-emerald-800 border-emerald-300 hover:border-emerald-500'
                                : 'bg-amber-50 text-amber-800 border-amber-400 border-dashed hover:border-amber-600'}`}
                        >
                            <span aria-hidden="true">{on ? '✓' : '~'}</span>
                            {k.label} {dateLabel}
                        </button>
                    );
                })}
            </div>
            <div className="text-[11px] text-slate-500">✓ confirmed with the client · ~ still tentative</div>
        </div>
    );
}

// Seed chip state from a move: confirmed unless the date is tentative.
export function seedDateChips(move) {
    const seed = {};
    if (move) dateKeysOf(move).forEach(k => { seed[k.key] = !k.tentative; });
    return seed;
}

// ─── Hook ───────────────────────────────────────────────────────────────

export function useEstimateStatusChange({ currentStatus, commit, isLocked = false, move = null, confirmDates }) {
    // Pending confirmation: null = nothing in flight; { newStatus, ...warning } = dialog open.
    const [pending, setPending] = useState(null);
    const [saving, setSaving] = useState(false);
    // Date chips on the Book confirm (2B): { load: true, unload: false, … }
    // — true = ✓ confirmed, false = ~ still tentative. Seeded from the
    // move's tentative flags when the dialog opens; tapping flips a chip.
    const [chipState, setChipState] = useState({});

    // The move's dates, in trip order (pack → load → unload). Only the Book
    // confirm shows them, and only when the caller handed us a move.
    const dateKeys = move ? dateKeysOf(move) : [];
    const showChips = !!pending && pending.newStatus === 'booked' && dateKeys.length > 0;

    // Caller invokes this with the desired new status. We check for a
    // warning; if one exists, open the dialog. Otherwise commit immediately.
    const request = (newStatus) => {
        if (isLocked) return;
        if (!currentStatus || currentStatus === newStatus) return;
        const warning = getEstimateStatusTransitionWarning(currentStatus, newStatus);
        if (warning) {
            if (newStatus === 'booked' && move) setChipState(seedDateChips(move));
            setPending({ newStatus, ...warning });
            return;
        }
        doCommit(newStatus);
    };

    // Actually performs the save. Used both for direct (no warning) and
    // post-confirmation (after user clicks confirm) commits. Caller's
    // commit() handles its own toasts / error messages.
    //
    // For a booking with `confirmKeys`, log those date confirmations FIRST
    // (confirmMoveDates clears the tentative flags and appends to the
    // move's confirmation log), then book. Keys that were already
    // confirmed are harmless to include — the log just gains an entry.
    const doCommit = async (newStatus, { confirmKeys = null } = {}) => {
        setSaving(true);
        try {
            if (confirmKeys && confirmKeys.length && typeof confirmDates === 'function') {
                await confirmDates(confirmKeys);
            }
            await commit(newStatus);
            setPending(null);
        } finally {
            setSaving(false);
        }
    };

    const confirmedCount = dateKeys.filter(k => chipState[k.key]).length;
    const keysNowConfirmed = dateKeys.filter(k => chipState[k.key]).map(k => k.key);

    const dialog = pending ? createPortal((
        <div
            className="fixed inset-0 z-[300] bg-black/60 flex items-center justify-center p-4"
            onClick={(e) => { e.stopPropagation(); if (!saving) setPending(null); }}
        >
            <div
                className="bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden"
                onClick={e => e.stopPropagation()}
            >
                <div className={`px-6 py-5 border-b border-slate-100 ${pending.kind === 'danger' ? 'bg-rose-50' : 'bg-amber-50'}`}>
                    <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-xl ${pending.kind === 'danger' ? 'bg-rose-200 text-rose-800' : 'bg-amber-200 text-amber-800'}`}>!</div>
                        <div>
                            <h3 className={`text-lg font-bold ${pending.kind === 'danger' ? 'text-rose-900' : 'text-amber-900'}`}>
                                {pending.title}
                            </h3>
                            <p className={`text-xs mt-0.5 ${pending.kind === 'danger' ? 'text-rose-700' : 'text-amber-700'}`}>
                                Review the side effects before continuing.
                            </p>
                        </div>
                    </div>
                </div>
                <div className="px-6 py-5 space-y-3 text-sm text-slate-700">
                    <p>{pending.body}</p>
                    {/* Date chips (showroom pick 2B, 2026-09-18): each of the
                        move's dates is a toggle — ✓ confirmed with the client
                        (emerald) or ~ still tentative (amber, dashed). Booking
                        logs the ✓ ones; nothing here blocks the booking. */}
                    {showChips && <DateConfirmChips dateKeys={dateKeys} state={chipState} onChange={setChipState} disabled={saving} />}
                    <p className="text-xs text-slate-500">
                        The audit log will record this change with your name and timestamp.
                    </p>
                </div>
                <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-3 flex-wrap">
                    <Button
                        variant="secondary"
                        onClick={() => setPending(null)}
                        disabled={saving}
                    >
                        Cancel
                    </Button>
                    {showChips ? (
                        <>
                            {/* Rule 4: "Book, confirm later" is always allowed —
                                books without touching any date. */}
                            <Button
                                variant="tertiary"
                                onClick={() => doCommit(pending.newStatus)}
                                disabled={saving}
                            >
                                Book, confirm later
                            </Button>
                            <Button
                                variant="primary"
                                onClick={() => doCommit(pending.newStatus, { confirmKeys: keysNowConfirmed })}
                                loading={saving}
                            >
                                Book — {confirmedCount} confirmed
                            </Button>
                        </>
                    ) : (
                        <Button
                            variant={pending.kind === 'danger' ? 'danger' : 'primary'}
                            onClick={() => doCommit(pending.newStatus)}
                            loading={saving}
                        >
                            {pending.confirmLabel}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    ), document.body) : null;

    return { request, dialog, saving };
}
